/**
 * @fileoverview ClubOS Alerts page — role-based, filterable system-wide alerts
 */

import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Auth context for role-based filtering
import { useAuth } from "context/AuthContext";

// Mock data
import { alerts } from "data/mockData";
import { CLUB_NAME } from "utils/indiaLocale";

// TODO: Replace with API call when backend is ready
// import { alertsService } from "api";

// ─── ROLE → VISIBLE CATEGORIES ────────────────────────────────────────────────
// Defines which alert categories each department can see.
const DEPT_CATEGORIES = {
  Management: ["Finance", "Squad", "Scouting", "Compliance"],
  Finance:    ["Finance", "Compliance"],
  Coaching:   ["Squad", "Compliance"],
  Scouting:   ["Scouting", "Squad"],
  Medical:    ["Squad"],
  IT:         ["Finance", "Squad", "Scouting", "Compliance"],
};

const alertIconMap = {
  error:   { icon: "error_outline",   color: "error"   },
  warning: { icon: "warning_amber",   color: "warning" },
  info:    { icon: "info_outline",    color: "info"    },
};

function Alerts() {
  const { currentUser } = useAuth();
  const allowedCategories = DEPT_CATEGORIES[currentUser.department] || DEPT_CATEGORIES.Management;

  // Tabs: "All" + each allowed category
  const TABS = ["All", ...allowedCategories];
  const [activeTab, setActiveTab] = useState(0);

  // Only show alerts the current role can see
  const roleFiltered = alerts.filter((a) => allowedCategories.includes(a.category));

  const selectedCategory = TABS[activeTab];
  const filtered = selectedCategory === "All"
    ? roleFiltered
    : roleFiltered.filter((a) => a.category === selectedCategory);

  const unreadCount = roleFiltered.filter((a) => !a.read).length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <SoftBox>
            <SoftBox display="flex" alignItems="center" gap={2} mb={0.5}>
              <SoftTypography variant="h4" fontWeight="bold" color="dark">
                Alerts
              </SoftTypography>
              {unreadCount > 0 && (
                <SoftBadge badgeContent={`${unreadCount} unread`} color="error" size="sm" container />
              )}
            </SoftBox>
            <SoftTypography variant="body2" color="text">
              {CLUB_NAME} · Showing alerts for <strong>{currentUser.role}</strong> ({currentUser.department})
            </SoftTypography>
          </SoftBox>
          {/* Visible categories pill strip */}
          <SoftBox display="flex" gap={0.75} flexWrap="wrap" alignItems="center">
            {allowedCategories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                size="small"
                sx={{ fontSize: "0.7rem", height: 22, backgroundColor: "#f0f4ff", color: "#344767" }}
              />
            ))}
          </SoftBox>
        </SoftBox>

        {/* ── Summary KPIs ────────────────────────────────────────────────────── */}
        <Grid container spacing={2} mb={3}>
          {[
            { label: "Total Visible",  count: roleFiltered.length,                                          icon: "notifications",  color: "info"    },
            { label: "Unread",         count: unreadCount,                                                  icon: "mark_unread_chat_alt", color: "error" },
            { label: "High Priority",  count: roleFiltered.filter(a => a.priority === "high").length,       icon: "priority_high",  color: "error"   },
            { label: "Action Required",count: roleFiltered.filter(a => !a.read && a.priority === "high").length, icon: "assignment_late", color: "warning" },
          ].map(({ label, count, icon, color }) => (
            <Grid item xs={6} sm={3} key={label}>
              <Card>
                <SoftBox p={2} display="flex" alignItems="center" gap={1.5}>
                  <SoftBox
                    bgColor={color}
                    variant="gradient"
                    width="2.5rem"
                    height="2.5rem"
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Icon sx={{ color: "white", fontSize: "1.1rem" }}>{icon}</Icon>
                  </SoftBox>
                  <SoftBox>
                    <SoftTypography variant="h5" fontWeight="bold" color="dark" lineHeight={1}>
                      {count}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text">{label}</SoftTypography>
                  </SoftBox>
                </SoftBox>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Alert List ──────────────────────────────────────────────────────── */}
        <Card>
          <SoftBox p={2}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ mb: 2, borderBottom: "1px solid #f0f0f0" }}
            >
              {TABS.map((cat) => {
                const cnt = cat === "All"
                  ? roleFiltered.length
                  : roleFiltered.filter(a => a.category === cat).length;
                return (
                  <Tab
                    key={cat}
                    label={`${cat} (${cnt})`}
                    sx={{ textTransform: "none", fontWeight: 500, fontSize: "0.8rem" }}
                  />
                );
              })}
            </Tabs>

            {filtered.length === 0 && (
              <SoftBox py={4} textAlign="center">
                <Icon sx={{ fontSize: "2rem", color: "#ccc", display: "block", mb: 1 }}>notifications_off</Icon>
                <SoftTypography variant="body2" color="text">
                  No alerts in this category.
                </SoftTypography>
              </SoftBox>
            )}

            {filtered.map((alert, i) => {
              const { icon, color } = alertIconMap[alert.type] || alertIconMap.info;
              return (
                <SoftBox key={alert.id}>
                  <SoftBox
                    display="flex"
                    alignItems="flex-start"
                    py={1.5}
                    sx={{
                      opacity: alert.read ? 0.6 : 1,
                      borderLeft: !alert.read ? `3px solid` : "3px solid transparent",
                      borderColor: !alert.read
                        ? (color === "error" ? "#ea0606" : color === "warning" ? "#fb8c00" : "#17c1e8")
                        : "transparent",
                      pl: !alert.read ? 1.5 : 0,
                      borderRadius: "4px",
                      transition: "all 0.15s",
                      "&:hover": { backgroundColor: "#f8f9fa" },
                    }}
                  >
                    {/* Icon */}
                    <SoftBox
                      bgColor={color}
                      variant="gradient"
                      width="2.5rem"
                      height="2.5rem"
                      borderRadius="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mr={2}
                      flexShrink={0}
                    >
                      <Icon sx={{ color: "white" }}>{icon}</Icon>
                    </SoftBox>

                    {/* Content */}
                    <SoftBox flex={1}>
                      <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <SoftTypography variant="button" fontWeight="bold">
                          {alert.title}
                        </SoftTypography>
                        <SoftBadge
                          badgeContent={alert.category}
                          color={color}
                          size="xs"
                          container
                        />
                        <SoftBadge
                          badgeContent={alert.priority}
                          color={alert.priority === "high" ? "error" : alert.priority === "medium" ? "warning" : "success"}
                          size="xs"
                          container
                        />
                        {!alert.read && (
                          <SoftBadge badgeContent="NEW" color="dark" size="xs" container />
                        )}
                      </SoftBox>
                      <SoftTypography variant="body2" color="text" mt={0.5}>
                        {alert.description}
                      </SoftTypography>
                      <SoftTypography variant="caption" color="secondary">
                        {alert.time}
                      </SoftTypography>
                    </SoftBox>

                    {/* Action */}
                    <SoftBox ml={2} flexShrink={0}>
                      <SoftButton variant="outlined" color={color} size="small">
                        {alert.actionLabel}
                      </SoftButton>
                    </SoftBox>
                  </SoftBox>
                  {i < filtered.length - 1 && <Divider sx={{ my: 0 }} />}
                </SoftBox>
              );
            })}
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />
    </DashboardLayout>
  );
}

export default Alerts;
